import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authPost, authFetch } from '../../lib/authFetch';
import { isLocalModeEnabled, getLocalDonors, getLocalDonorByName, updateLocalDonor, deleteLocalDonor, addLocalDonor } from '../../lib/localMode';
import './DesktopProfileView.css';

// All three /api/donor-update call sites in this file share this field/value
// shape - handling it here once instead of three separate branches. Mirrors
// what the server endpoint does for the same field values, just against
// localStorage instead of Supabase.
function localDonorUpdate(donorName, field, value) {
  if (field === 'contact_info') {
    const { newName, ...updates } = value;
    if (newName && newName !== donorName) {
      const donor = getLocalDonorByName(donorName);
      deleteLocalDonor(donorName);
      addLocalDonor({ ...donor, ...updates, name: newName });
      return { newName };
    }
    updateLocalDonor(donorName, updates);
    return {};
  }
  updateLocalDonor(donorName, { [field]: value });
  return {};
}

// Editable text component - click to edit, with AI narrative auto-update
// eslint-disable-next-line no-unused-vars
function EditableText({
  value,
  onSave,
  className = '',
  placeholder = 'Click to edit',
  type = 'text',
  triggerAI = true,
  label = ''
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEditing && !isSaving) {
      setEditValue(value || '');
    }
  }, [value, isEditing, isSaving]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!isEditing && !isSaving) {
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsEditing(false);
    setIsSaving(true);

    try {
      await onSave(editValue, { triggerAI });
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className={`editable-field editing ${className}`}>
        {label && <span className="editable-label">{label}</span>}
        <input
          ref={inputRef}
          type={type}
          className="editable-input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          placeholder={placeholder}
        />
      </div>
    );
  }

  if (isSaving) {
    return (
      <div className={`editable-field saving ${className}`}>
        {label && <span className="editable-label">{label}</span>}
        <span className="editable-value">{editValue}</span>
        <span className="editable-saving-indicator" />
      </div>
    );
  }

  return (
    <div className={`editable-field ${className}`} onClick={handleClick}>
      {label && <span className="editable-label">{label}</span>}
      <span className="editable-value">
        {value || <span className="editable-placeholder">{placeholder}</span>}
      </span>
      <span className="editable-edit-icon">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </span>
    </div>
  );
}

// Word reveal animation component
function WordReveal({ text, animate = true }) {
  const [visibleWords, setVisibleWords] = useState(animate ? 0 : Infinity);
  // Strip [[Name]] brackets for clean display during animation
  const cleanText = text.replace(/\[\[([^\]]+)\]\]/g, '$1');
  const words = cleanText.split(/(\s+)/);
  const actualWords = words.filter(w => w.trim());
  const totalWords = actualWords.length;

  useEffect(() => {
    if (!animate) return;
    setVisibleWords(0);

    const interval = setInterval(() => {
      setVisibleWords(prev => {
        if (prev >= totalWords) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 40);

    return () => clearInterval(interval);
  }, [text, animate, totalWords]);

  let wordIndex = 0;
  return (
    <span className="word-reveal-text">
      {words.map((word, i) => {
        if (!word.trim()) {
          return <span key={i}>{word}</span>;
        }
        const isVisible = wordIndex < visibleWords;
        wordIndex++;
        return (
          <span key={i} className={`reveal-word ${isVisible ? 'visible' : ''}`}>
            {word}
          </span>
        );
      })}
    </span>
  );
}

function DesktopProfileView({ donorName, donorData, groups = [], onClose, onNavigateToPerson, dataMode = 'real', narrativeUpdate, onRefreshDonors }) {
  const [fetchedData, setFetchedData] = useState(null);
  const [fetchedDataOwner, setFetchedDataOwner] = useState(null); // Track which person fetchedData belongs to
  // No loading state - show data immediately like mobile does
  const [, setRefreshKey] = useState(0);
  const [isResearching, setIsResearching] = useState(false);
  const [researchMessage, setResearchMessage] = useState(null);
  const [researchOptions, setResearchOptions] = useState(null);
  const [isConfirmationMode, setIsConfirmationMode] = useState(false); // true = ready to save, false = disambiguation
  const researchInProgressRef = useRef(false);
  const [newNoteText, setNewNoteText] = useState(null);
  const [narrativeUpdated, setNarrativeUpdated] = useState(false);
  const [pendingDetails, setPendingDetails] = useState(null); // Queue details to show after narrative animation
  const [showDetails, setShowDetails] = useState(true); // Control details visibility for staggered reveal
  const [, setDetailsLoading] = useState(false); // Loading state for post-update timeline fetch
  const previousNarrativeRef = useRef('');
  const storyRef = useRef(null);
  const narrativeAnimatingRef = useRef(false);

  // Use donorData prop immediately - exactly like mobile does
  // Only use fetchedData if it belongs to the current person (prevents stale data on profile switch)
  const effectiveData = (fetchedDataOwner === donorName ? fetchedData : null) || donorData || {};

  // Track if we're generating timeline in background (for skeleton display)
  const [isGenerating, setIsGenerating] = useState(false);

  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  // Inline editing state: { type: 'narrative' | 'timeline' | 'todo', index?: number, value: string }
  const [editingItem, setEditingItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(null); // { type, index } - tracks which item is being saved
  const editInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Contact edit modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    organization: '',
    title: ''
  });
  const [savingContact, setSavingContact] = useState(false);

  // Reset all state when donor changes so we use fresh donorData
  useEffect(() => {
    setFetchedData(null);
    setFetchedDataOwner(null);
    setDocuments([]);
    setEditingItem(null);
    setSavingEdit(null);
    setContactModalOpen(false);
    setNewNoteText(null);
    setNarrativeUpdated(false);
    previousNarrativeRef.current = '';
  }, [donorName]);

  // Handle Escape key to close modals and cancel editing
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingItem) {
          setEditingItem(null);
        } else if (contactModalOpen) {
          setContactModalOpen(false);
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [contactModalOpen, editingItem]);

  // Extract key details from narrative as fallback (like mobile)
  const extractKeyDetails = (narrativeText) => {
    if (!narrativeText) return [];
    const details = [];
    const text = narrativeText.toLowerCase();

    const interests = ['arts', 'education', 'music', 'sports', 'athletics', 'science', 'research',
                       'scholarship', 'building', 'campus', 'library', 'technology', 'stem',
                       'youth', 'children', 'health', 'medical', 'environmental', 'sustainability'];
    interests.forEach(interest => {
      if (text.includes(interest)) {
        details.push({ label: interest.charAt(0).toUpperCase() + interest.slice(1), type: 'interest' });
      }
    });

    return details.slice(0, 6);
  };

  // Handle inline field edits - triggers AI to update narrative
  // eslint-disable-next-line no-unused-vars
  const handleFieldUpdate = useCallback(async (field, value, options = {}) => {
    if (!donorName) return;

    const { triggerAI = true } = options;

    try {
      if (isLocalModeEnabled()) {
        localDonorUpdate(donorName, field, value);
      } else {
        await authPost('/api/donor-update', {
          donorName,
          field,
          value,
          mode: dataMode,
          updateNarrative: triggerAI && field !== 'narrative'
        });
      }

      // Update local state immediately
      setFetchedData(prev => prev ? { ...prev, [field]: value } : { [field]: value });
      setFetchedDataOwner(donorName);

      // Trigger refresh to sync
      setTimeout(() => setRefreshKey(prev => prev + 1), triggerAI ? 500 : 100);
    } catch (error) {
      console.error('Error updating field:', error);
    }
  }, [donorName, dataMode]);

  // Start inline editing
  const startEditing = (type, value, index = null) => {
    setEditingItem({ type, value, index });
    // Focus the contentEditable element and place cursor at end
    setTimeout(() => {
      const el = editInputRef.current;
      if (el) {
        el.focus();
        // Place cursor at end of text
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }, 50);
  };

  // Save inline edit - sends change through AI to update narrative and profile
  const saveEdit = async () => {
    if (!editingItem) return;

    const { type, value, index } = editingItem;

    // Set saving state to show loading animation
    setSavingEdit({ type, index });
    setEditingItem(null);

    // Construct a note describing the change for AI processing
    let noteMessage = '';

    if (type === 'narrative') {
      // Direct narrative edit - this IS the new narrative
      noteMessage = `Please update the narrative to: ${value}`;
    } else if (type === 'todo' && index !== null) {
      const oldTask = effectiveData?.tasks?.[index];
      const oldText = typeof oldTask === 'string' ? oldTask : oldTask?.text || '';
      if (oldText !== value) {
        noteMessage = `Task update: Changed "${oldText}" to "${value}"`;
      } else {
        setSavingEdit(null);
        return;
      }
    } else if (type === 'timeline' && index !== null) {
      const oldEvent = effectiveData?.timelineEvents?.[index]?.event || effectiveData?.timeline_events?.[index]?.event || '';
      if (oldEvent !== value) {
        noteMessage = `Timeline update: Changed "${oldEvent}" to "${value}"`;
      } else {
        setSavingEdit(null);
        return;
      }
    }

    if (!noteMessage) {
      setSavingEdit(null);
      return;
    }

    console.log('Saving edit:', { type, index, noteMessage, donorName });

    try {
      // Use save-note API to process through AI and update narrative
      const response = await authPost('/api/save-note', {
        message: noteMessage,
        donorName: donorName
      });

      console.log('Save note response:', response?.ok, response?.status);

      if (response?.ok) {
        const data = await response.json();
        console.log('Save note data:', data);

        // Update local state with AI-processed data
        // API returns: narrative, timelineEvents, keyDetails, tasks
        setFetchedData(prev => ({
          ...prev,
          narrative: data.narrative || prev?.narrative,
          keyDetails: data.keyDetails || prev?.keyDetails,
          tasks: data.tasks || prev?.tasks,
          timelineEvents: data.timelineEvents || prev?.timelineEvents
        }));
        setFetchedDataOwner(donorName);

        // Trigger animation for narrative update
        if (data.narrative) {
          previousNarrativeRef.current = data.narrative;
          setNewNoteText(data.narrative);
          setNarrativeUpdated(true);
          narrativeAnimatingRef.current = true;

          // Scroll to story section
          setTimeout(() => {
            storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);

          // Calculate animation duration based on word count
          const wordCount = data.narrative.split(/\s+/).length;
          const animationDuration = Math.max(wordCount * 30 + 500, 2000);

          setTimeout(() => {
            setNewNoteText(null);
            setNarrativeUpdated(false);
            narrativeAnimatingRef.current = false;
          }, animationDuration);
        }
      }
    } catch (error) {
      console.error('Error saving edit:', error);
    } finally {
      setSavingEdit(null);
    }
  };

  // Cancel inline edit
  const cancelEdit = () => {
    setEditingItem(null);
  };

  // Open contact edit modal
  const openContactModal = () => {
    setContactForm({
      name: donorName || '',
      email: effectiveData?.email || '',
      phone: effectiveData?.phone || '',
      address: effectiveData?.address || '',
      organization: effectiveData?.organization || '',
      title: effectiveData?.title || ''
    });
    setContactModalOpen(true);
  };

  // Save contact info
  const saveContactInfo = async () => {
    setSavingContact(true);
    try {
      // Update each field that changed
      const updates = {};
      if (contactForm.email !== (effectiveData?.email || '')) updates.email = contactForm.email;
      if (contactForm.phone !== (effectiveData?.phone || '')) updates.phone = contactForm.phone;
      if (contactForm.address !== (effectiveData?.address || '')) updates.address = contactForm.address;
      if (contactForm.organization !== (effectiveData?.organization || '')) updates.organization = contactForm.organization;
      if (contactForm.title !== (effectiveData?.title || '')) updates.title = contactForm.title;

      // Handle name change separately (requires special handling)
      const nameChanged = contactForm.name !== donorName;

      if (Object.keys(updates).length > 0 || nameChanged) {
        const value = { ...updates, newName: nameChanged ? contactForm.name : undefined };
        const result = isLocalModeEnabled()
          ? localDonorUpdate(donorName, 'contact_info', value)
          : await (await authPost('/api/donor-update', { donorName, field: 'contact_info', value, mode: dataMode })).json();

        // Update local state
        setFetchedData(prev => ({
          ...prev,
          ...updates
        }));
        setFetchedDataOwner(donorName);

        // Refresh donors list to pick up changes
        if (onRefreshDonors) {
          onRefreshDonors();
        }

        // If name changed, navigate to new profile
        if (result.newName && onNavigateToPerson) {
          setContactModalOpen(false);
          onNavigateToPerson(result.newName);
          return;
        }
      }

      setContactModalOpen(false);
    } catch (error) {
      console.error('Error saving contact info:', error);
    } finally {
      setSavingContact(false);
    }
  };

  // Track what we've fetched to prevent duplicate requests (person + narrative hash)
  const timelineFetchedRef = useRef(null);

  // Get the current narrative from either fetchedData or donorData
  const currentNarrative = fetchedData?.narrative || donorData?.narrative || '';

  // Generate timeline when profile loads or narrative changes
  useEffect(() => {
    if (!donorName || !currentNarrative) return;

    // Create a key based on person name + narrative length to detect changes
    const fetchKey = `${donorName}:${currentNarrative.length}`;

    // Skip if we already fetched for this exact state
    if (timelineFetchedRef.current === fetchKey) {
      return;
    }

    // Also skip if we already have timeline data in donorData (from cache)
    if (donorData?.timelineEvents?.length > 0) {
      setIsGenerating(false);
      return;
    }

    // Generate timeline
    const generateTimeline = async () => {
      timelineFetchedRef.current = fetchKey; // Mark as fetched
      setIsGenerating(true);
      try {
        const local = isLocalModeEnabled();
        const response = await authPost('/api/donor-timeline', {
          donorName,
          mode: dataMode,
          forceRefresh: true,
          ...(local ? { localMode: true, localDonors: getLocalDonors() } : {})
        });
        if (response?.ok) {
          const data = await response.json();
          setFetchedData(prev => ({
            ...(prev || donorData || {}),
            timelineEvents: data.timeline || [],
            keyDetails: data.keyDetails || [],
            tasks: data.tasks || []
          }));
          setFetchedDataOwner(donorName);
          if (local) {
            // Local Mode: the server doesn't persist this, so the client caches it
            // on the local donor record itself instead.
            if (data.timeline?.length || data.keyDetails?.length || data.tasks?.length) {
              updateLocalDonor(donorName, {
                timeline_events: data.timeline || [],
                key_details: data.keyDetails || [],
                tasks: data.tasks || []
              });
            }
          } else if (onRefreshDonors && !data.cached) {
            // Refresh parent's donors data so timeline is cached for next navigation
            onRefreshDonors();
          }
        }
      } catch (err) {
        console.error('Error generating timeline:', err);
      } finally {
        setIsGenerating(false);
      }
    };
    generateTimeline();
  }, [donorName, currentNarrative, dataMode, onRefreshDonors, donorData?.timelineEvents?.length]);

  // Handle narrative updates pushed from Feed (like mobile)
  useEffect(() => {
    if (!narrativeUpdate?.personName) return;

    // Check if this update is for the current person
    const updateName = narrativeUpdate.personName?.toLowerCase().trim();
    const currentName = donorName?.toLowerCase().trim();
    if (updateName && currentName && updateName !== currentName) return;

    // Handle narrative update - only if it's actually different
    const isNewNarrative = narrativeUpdate.narrative &&
      narrativeUpdate.narrative !== fetchedData?.narrative &&
      narrativeUpdate.narrative !== previousNarrativeRef.current;

    if (isNewNarrative) {
      // Update the narrative immediately
      setFetchedData(prev => ({
        ...prev,
        narrative: narrativeUpdate.narrative
      }));
      setFetchedDataOwner(donorName);

      previousNarrativeRef.current = narrativeUpdate.narrative;
      setNewNoteText(narrativeUpdate.narrative);
      setNarrativeUpdated(true);
      narrativeAnimatingRef.current = true;

      // Hide details while narrative animates - they'll cascade after
      setShowDetails(false);

      // Scroll to story section
      setTimeout(() => {
        storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      // Start timeline fetch IMMEDIATELY in parallel with animation
      const localForTimeline = isLocalModeEnabled();
      const timelinePromise = authPost('/api/donor-timeline', {
        donorName,
        mode: dataMode,
        forceRefresh: true,
        ...(localForTimeline ? { localMode: true, localDonors: getLocalDonors() } : {})
      }).then(async (response) => {
        if (response?.ok) {
          return response.json();
        }
        return null;
      }).catch(err => {
        console.error('Error fetching timeline after update:', err);
        return null;
      });

      // Clear animation after it plays - then show details with stagger
      const wordCount = narrativeUpdate.narrative.split(/\s+/).length;
      const animationDuration = Math.max(wordCount * 30 + 500, 2000); // Slightly faster
      setTimeout(async () => {
        setNewNoteText(null);
        setNarrativeUpdated(false);
        narrativeAnimatingRef.current = false;

        // Apply any pending details that arrived during animation
        if (pendingDetails) {
          setFetchedData(prev => ({
            ...prev,
            keyDetails: pendingDetails.keyDetails || prev.keyDetails,
            tasks: pendingDetails.tasks || prev.tasks
          }));
          setFetchedDataOwner(donorName);
          setPendingDetails(null);
        }

        // Show details with stagger animation
        setShowDetails(true);
        setDetailsLoading(true);

        // Wait for timeline data (should already be ready since it started in parallel)
        const timelineData = await timelinePromise;
        if (timelineData) {
          setFetchedData(prev => ({
            ...prev,
            timelineEvents: timelineData.timeline?.length > 0 ? timelineData.timeline : prev.timelineEvents,
            keyDetails: timelineData.keyDetails?.length > 0 ? timelineData.keyDetails : prev.keyDetails,
            tasks: timelineData.tasks?.length > 0 ? timelineData.tasks : prev.tasks
          }));
          setFetchedDataOwner(donorName);
          if (localForTimeline && (timelineData.timeline?.length || timelineData.keyDetails?.length || timelineData.tasks?.length)) {
            // Local Mode: the server doesn't persist this, so cache it on the local record
            updateLocalDonor(donorName, {
              timeline_events: timelineData.timeline || [],
              key_details: timelineData.keyDetails || [],
              tasks: timelineData.tasks || []
            });
          }
        }
        setDetailsLoading(false);
      }, animationDuration);
    }

    // Handle key details and tasks update (can come separately after narrative)
    if (narrativeUpdate.keyDetails || narrativeUpdate.tasks) {
      if (narrativeAnimatingRef.current) {
        // Queue details to show after narrative animation finishes
        setPendingDetails({
          keyDetails: narrativeUpdate.keyDetails,
          tasks: narrativeUpdate.tasks
        });
      } else {
        // Not animating, show immediately
        setFetchedData(prev => ({
          ...prev,
          keyDetails: narrativeUpdate.keyDetails || prev.keyDetails,
          tasks: narrativeUpdate.tasks || prev.tasks
        }));
        setFetchedDataOwner(donorName);
      }
    }
  }, [narrativeUpdate, donorName]);

  // Fetch documents when profile opens
  useEffect(() => {
    if (!donorData?.id) return;

    const fetchDocuments = async () => {
      try {
        const response = await authFetch(`/api/documents/${donorData.id}`);
        if (response.ok) {
          const data = await response.json();
          setDocuments(data.documents || []);
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
      }
    };
    fetchDocuments();
  }, [donorData?.id]);

  // Handle document upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !donorData?.id) return;

    setIsUploading(true);
    setUploadProgress('Uploading...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('donorId', donorData.id);
      formData.append('personName', donorName);

      const response = await authFetch('/api/documents/upload', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(prev => [data.document, ...prev]);
        setUploadProgress('Uploaded!');
        setTimeout(() => setUploadProgress(null), 2000);
      } else {
        const error = await response.json();
        setUploadProgress(`Error: ${error.error || 'Upload failed'}`);
        setTimeout(() => setUploadProgress(null), 3000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadProgress('Upload failed');
      setTimeout(() => setUploadProgress(null), 3000);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle document download
  const handleDocumentDownload = async (doc) => {
    try {
      const response = await authFetch(`/api/documents/file/${doc.id}`);
      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Handle document delete
  const handleDocumentDelete = async (docId) => {
    try {
      const response = await authFetch(`/api/documents/${docId}`, { method: 'DELETE' });
      if (response.ok) {
        setDocuments(prev => prev.filter(d => d.id !== docId));
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get group for this person
  const personGroup = groups.find(g => g.id === effectiveData?.groupId);

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get avatar color based on name (cool colors only)
  const getAvatarColor = (name) => {
    const colors = ['#a78bfa', '#60a5fa', '#38bdf8', '#2dd4bf', '#818cf8'];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  // Format relative date (handles both past and future dates)
  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';

    // If it's a natural language date without a year (like "April 1" or "next Tuesday"),
    // just display it as-is instead of trying to parse it
    const hasYear = /\b20\d{2}\b/.test(dateStr) || /^\d{4}-\d{2}-\d{2}/.test(dateStr);
    const isISOFormat = /^\d{4}-\d{2}-\d{2}/.test(dateStr);

    // Only try to parse if it looks like a proper date format with a year
    if (!hasYear && !isISOFormat) {
      return dateStr; // Return natural language dates as-is
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    // Sanity check: if the parsed year is more than 2 years off, something went wrong
    const now = new Date();
    const yearDiff = now.getFullYear() - date.getFullYear();
    if (yearDiff > 2 || yearDiff < -2) return dateStr; // Return as-is if date seems wrong

    const diffMs = now - date;
    const diffDays = Math.floor(Math.abs(diffMs) / 86400000);
    const isFuture = diffMs < 0;

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return isFuture ? 'Tomorrow' : 'Yesterday';
    if (diffDays < 7) return isFuture ? `In ${diffDays}d` : `${diffDays}d ago`;
    if (diffDays < 30) return isFuture ? `In ${Math.floor(diffDays / 7)}w` : `${Math.floor(diffDays / 7)}w ago`;
    return isFuture ? `In ${Math.floor(diffDays / 30)}mo` : `${Math.floor(diffDays / 30)}mo ago`;
  };

  // Get event type color
  const getEventColor = (event) => {
    const text = (event?.event || '').toLowerCase();
    if (text.includes('meeting') || text.includes('coffee') || text.includes('lunch')) return '#4ade80';
    if (text.includes('call') || text.includes('phone')) return '#60a5fa';
    if (text.includes('gala') || text.includes('event')) return '#a78bfa';
    return '#666';
  };

  // Highlight narrative text with person links and keyword highlights
  const highlightNarrative = (text) => {
    if (!text) return null;

    const interestKeywords = ['arts', 'education', 'music', 'sports', 'athletics', 'science', 'research',
                              'scholarship', 'building', 'campus', 'library', 'technology', 'stem',
                              'youth', 'children', 'health', 'medical', 'environmental', 'sustainability'];
    const familyKeywords = ['wife', 'husband', 'spouse', 'daughter', 'son', 'brother', 'sister', 'family'];
    const allKeywords = [...interestKeywords, ...familyKeywords];

    // Process a single paragraph's text with highlighting
    const processParagraph = (paragraphText, paragraphIdx) => {
      // Extract [[Person Name]] links
      const personLinkPattern = /\[\[([^\]]+)\]\]/g;
      const segments = [];
      let lastIndex = 0;
      let linkMatch;

      while ((linkMatch = personLinkPattern.exec(paragraphText)) !== null) {
        if (linkMatch.index > lastIndex) {
          segments.push({ text: paragraphText.slice(lastIndex, linkMatch.index), type: 'text' });
        }
        segments.push({ text: linkMatch[1], type: 'personLink' });
        lastIndex = linkMatch.index + linkMatch[0].length;
      }
      if (lastIndex < paragraphText.length) {
        segments.push({ text: paragraphText.slice(lastIndex), type: 'text' });
      }

      // Process each segment
      const result = [];
      segments.forEach((segment, segIdx) => {
        if (segment.type === 'personLink') {
          result.push(
            <span
              key={`p${paragraphIdx}-link-${segIdx}`}
              className="narrative-person-link"
              onClick={() => onNavigateToPerson && onNavigateToPerson(segment.text)}
            >
              {segment.text}
            </span>
          );
        } else {
          // Apply keyword highlighting
          const combinedPattern = new RegExp(
            `(\\$[\\d,]+[kKmM]?|\\b\\d{1,3}(?:,\\d{3})+(?:[kKmM])?\\b|\\b\\d+[kKmM]\\b|\\b(?:${allKeywords.join('|')})\\b)`,
            'gi'
          );

          const parts = [];
          let partLastIndex = 0;
          let match;
          const segText = segment.text;

          while ((match = combinedPattern.exec(segText)) !== null) {
            if (match.index > partLastIndex) {
              parts.push({ text: segText.slice(partLastIndex, match.index), type: 'normal' });
            }

            const matchText = match[1];
            let type = 'normal';
            if (matchText.startsWith('$') || /^\d/.test(matchText)) {
              type = 'amount';
            } else if (interestKeywords.some(k => k.toLowerCase() === matchText.toLowerCase())) {
              type = 'interest';
            } else if (familyKeywords.some(k => k.toLowerCase() === matchText.toLowerCase())) {
              type = 'family';
            }

            parts.push({ text: matchText, type });
            partLastIndex = match.index + matchText.length;
          }

          if (partLastIndex < segText.length) {
            parts.push({ text: segText.slice(partLastIndex), type: 'normal' });
          }

          parts.forEach((part, i) => {
            if (part.type === 'normal') {
              result.push(<span key={`p${paragraphIdx}-${segIdx}-${i}`}>{part.text}</span>);
            } else {
              result.push(
                <span key={`p${paragraphIdx}-${segIdx}-${i}`} className={`narrative-highlight ${part.type}`}>
                  {part.text}
                </span>
              );
            }
          });
        }
      });

      return result;
    };

    // Split text into paragraphs on double newlines
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim());

    // If only one paragraph, return without wrapper
    if (paragraphs.length === 1) {
      return processParagraph(paragraphs[0], 0);
    }

    // Multiple paragraphs - wrap each in a p tag
    return paragraphs.map((para, idx) => (
      <p key={`para-${idx}`} className="narrative-paragraph">
        {processParagraph(para.trim(), idx)}
      </p>
    ));
  };

  // Handle research - uses same endpoint as Add Person modal for consistent results
  const handleResearch = async () => {
    if (!donorName || researchInProgressRef.current) return;

    researchInProgressRef.current = true;
    setIsResearching(true);
    setResearchOptions(null);
    setResearchMessage(null);
    setIsConfirmationMode(false);

    const currentNarrative = effectiveData?.narrative || '';

    try {
      // Use the same endpoint as Add Person modal - it returns full info for each match
      const response = await authPost('/api/research-person', {
        personName: donorName,
        existingInfo: currentNarrative ? `Existing notes: ${currentNarrative}` : ''
      });

      if (response.ok) {
        const data = await response.json();

        // Handle matches - each match has full info ready to save
        if (data.matches && data.matches.length > 0) {
          // Convert to our options format - use 'info' as description (full research text)
          const options = data.matches.map(match => ({
            name: match.name,
            summary: match.summary,
            description: match.info // Full research text, ready to save
          }));
          setResearchOptions(options);
          setResearchMessage(options.length > 1 ? 'Select the right person:' : 'Add this to profile?');
          setIsConfirmationMode(true); // All options have full info, ready to save on click
          return;
        }

        // Handle no matches
        if (data.notFound) {
          setResearchMessage(data.reason || 'No information found.');
          setTimeout(() => setResearchMessage(null), 3000);
          return;
        }

        setResearchMessage('No new information found.');
        setTimeout(() => setResearchMessage(null), 3000);
      } else {
        setResearchMessage('Research failed. Please try again.');
        setTimeout(() => setResearchMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error researching donor:', error);
      setResearchMessage('Research failed. Please try again.');
      setTimeout(() => setResearchMessage(null), 3000);
    } finally {
      researchInProgressRef.current = false;
      setIsResearching(false);
    }
  };

  const handleResearchOptionSelect = async (option) => {
    if (isConfirmationMode) {
      // Confirmation mode - save the insight directly
      setResearchOptions(null);
      setResearchMessage(null);
      setIsConfirmationMode(false);

      try {
        const currentNarrative = effectiveData?.narrative || '';
        const insight = option.description || option.insight;
        console.log('[Research Save] Saving insight for', donorName, '- insight:', insight?.substring(0, 100));

        // Check for empty insight
        if (!insight || !insight.trim()) {
          console.error('[Research Save] Empty insight, cannot save');
          setResearchMessage('No research data to save.');
          setTimeout(() => setResearchMessage(null), 3000);
          return;
        }

        const updatedNarrative = currentNarrative
          ? currentNarrative + '\n\n' + insight
          : insight;

        if (isLocalModeEnabled()) {
          localDonorUpdate(donorName, 'narrative', updatedNarrative);
        } else {
          const response = await authPost('/api/donor-update', {
            donorName,
            field: 'narrative',
            value: updatedNarrative,
            mode: dataMode
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('[Research Save] API error:', response.status, errorData);
            setResearchMessage('Failed to save. Please try again.');
            setTimeout(() => setResearchMessage(null), 3000);
            return;
          }
        }

        console.log('[Research Save] Successfully saved research for', donorName);
        setTimeout(() => setRefreshKey(prev => prev + 1), 300);
      } catch (error) {
        console.error('[Research Save] Error:', error);
        setResearchMessage('Failed to save. Please try again.');
        setTimeout(() => setResearchMessage(null), 3000);
      }
    }
  };

  // Same fallbacks as mobile - check both camelCase and snake_case
  const timelineEvents = effectiveData?.timelineEvents || effectiveData?.timeline_events || donorData?.timelineEvents || donorData?.timeline_events || [];
  const keyDetails = effectiveData?.keyDetails || effectiveData?.key_details || donorData?.keyDetails || donorData?.key_details || [];
  const tasks = effectiveData?.tasks || donorData?.tasks || [];

  return (
    <div className="desktop-profile-view">
      {/* Header */}
      <div className="profile-header">
        <div
          className="profile-avatar"
          style={{ borderColor: getAvatarColor(donorName), color: getAvatarColor(donorName) }}
        >
          {getInitials(donorName)}
        </div>
        <h2 className="profile-name clickable" onClick={openContactModal}>
          {donorName}
          <svg className="edit-hint" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </h2>
        {personGroup && (
          <div className="profile-group" style={{ color: personGroup.color || '#a78bfa' }}>
            {personGroup.name}
          </div>
        )}
        <button
          className={`profile-research-btn ${isResearching ? 'loading' : ''}`}
          onClick={() => handleResearch()}
          disabled={isResearching || researchOptions}
        >
          {isResearching ? 'Researching...' : 'Research'}
        </button>
        {researchMessage && !researchOptions && (
          <p className="research-message">{researchMessage}</p>
        )}
        {researchOptions && (
          <div className="research-options">
            <p className="research-options-label">{researchMessage}</p>
            {researchOptions.map((option, idx) => (
              <button
                key={idx}
                className="research-option-btn"
                onClick={() => handleResearchOptionSelect(option)}
              >
                <span className="research-option-name">{option.name}</span>
                <span className="research-option-desc">{option.summary || option.description}</span>
              </button>
            ))}
            <button
              className="research-option-cancel"
              onClick={() => { setResearchOptions(null); setResearchMessage(null); setIsConfirmationMode(false); }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Two-column layout: Story on left, Timeline/Todo on right */}
      <div className="profile-columns">
        {/* Left column: Story and Key Details */}
        <div className="profile-left-column">
          {/* Story */}
          {(effectiveData?.narrative || newNoteText) && (
            <div className="profile-section" ref={storyRef}>
              <div className="section-label">The Story</div>
              <div
                className={`profile-story editable-content ${narrativeUpdated ? 'narrative-updated' : ''} ${editingItem?.type === 'narrative' ? 'editing' : ''} ${savingEdit?.type === 'narrative' ? 'saving' : ''}`}
                contentEditable={editingItem?.type === 'narrative'}
                suppressContentEditableWarning={true}
                ref={editingItem?.type === 'narrative' ? editInputRef : null}
                onInput={(e) => editingItem?.type === 'narrative' && setEditingItem({ ...editingItem, value: e.currentTarget.innerText })}
                onKeyDown={(e) => {
                  if (editingItem?.type === 'narrative') {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      saveEdit();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      e.stopPropagation();
                      cancelEdit();
                    }
                  }
                }}
              >
                {newNoteText ? (
                  <WordReveal text={newNoteText} animate={true} />
                ) : (
                  highlightNarrative(effectiveData.narrative)
                )}
                {!newNoteText && !editingItem && (
                  <button
                    className="hover-edit-icon"
                    onClick={() => startEditing('narrative', effectiveData.narrative)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                )}
                {editingItem?.type === 'narrative' && (
                  <button className="edit-confirm-btn" onClick={(e) => { e.stopPropagation(); saveEdit(); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Key Details - show skeleton while generating, or show data */}
          {showDetails && (isGenerating || keyDetails.length > 0 || extractKeyDetails(effectiveData?.narrative).length > 0) && (
            <div className="profile-section">
              <div className="section-label">Key Details</div>
              <div className="profile-key-details">
                {isGenerating && keyDetails.length === 0 && extractKeyDetails(effectiveData?.narrative).length === 0 ? (
                  <>
                    <span className="key-detail-tag skeleton">Loading</span>
                    <span className="key-detail-tag skeleton">Details</span>
                    <span className="key-detail-tag skeleton">Info</span>
                  </>
                ) : (
                  (keyDetails.length > 0 ? keyDetails : extractKeyDetails(effectiveData?.narrative)).map((detail, idx) => (
                    <span key={idx} className={`key-detail-tag ${detail.type || 'default'}`}>
                      {detail.label}
                    </span>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Timeline, To Do, and Documents */}
        <div className="profile-right-column">
          {/* Timeline - always show section */}
          {showDetails && (
            <div className="profile-section">
              <div className="section-label">Timeline</div>
              <div className="profile-timeline">
                <div className="timeline-line" />
                {isGenerating ? (
                  <>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="timeline-entry skeleton">
                        <div className="timeline-dot skeleton" />
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="timeline-title skeleton">Loading event</span>
                            <span className="timeline-date skeleton">--</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : timelineEvents.length === 0 ? (
                  <div className="timeline-empty">
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>No dated events yet. Add notes with dates like "met last week" or "call scheduled for Friday".</span>
                  </div>
                ) : (
                  timelineEvents.slice(0, 8).map((event, idx) => (
                    <div key={idx} className="timeline-entry">
                      <div
                        className="timeline-dot"
                        style={{ background: getEventColor(event) }}
                      />
                      <div className="timeline-content">
                        <div
                          className={`timeline-header editable-content ${editingItem?.type === 'timeline' && editingItem?.index === idx ? 'editing' : ''} ${savingEdit?.type === 'timeline' && savingEdit?.index === idx ? 'saving' : ''}`}
                        >
                          <span
                            className="timeline-title"
                            contentEditable={editingItem?.type === 'timeline' && editingItem?.index === idx}
                            suppressContentEditableWarning={true}
                            ref={editingItem?.type === 'timeline' && editingItem?.index === idx ? editInputRef : null}
                            onInput={(e) => editingItem?.type === 'timeline' && editingItem?.index === idx && setEditingItem({ ...editingItem, value: e.currentTarget.innerText })}
                            onKeyDown={(e) => {
                              if (editingItem?.type === 'timeline' && editingItem?.index === idx) {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  saveEdit();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  cancelEdit();
                                }
                              }
                            }}
                          >
                            {event.event}
                          </span>
                          <span className="timeline-date">{formatRelativeDate(event.date)}</span>
                          {!(editingItem?.type === 'timeline' && editingItem?.index === idx) && (
                            <button
                              className="hover-edit-icon"
                              onClick={() => !editingItem && startEditing('timeline', event.event, idx)}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                          )}
                          {editingItem?.type === 'timeline' && editingItem?.index === idx && (
                            <button className="edit-confirm-btn" onClick={(e) => { e.stopPropagation(); saveEdit(); }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                        {event.detail && (
                          <p className="timeline-detail">{event.detail}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Tasks - show skeleton while generating, or show data */}
          {showDetails && (isGenerating || tasks.length > 0) && (
            <div className="profile-section">
              <div className="section-label">To Do</div>
              {isGenerating && tasks.length === 0 ? (
                <>
                  {[1, 2].map((i) => (
                    <div key={i} className="profile-todo-item skeleton">
                      <div className="todo-checkbox skeleton" />
                      <div className="todo-content">
                        <div className="todo-text skeleton">Loading task</div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                tasks.filter(Boolean).map((task, idx) => {
                  const taskText = typeof task === 'string' ? task : (task?.text || '');
                  const isEditingThis = editingItem?.type === 'todo' && editingItem?.index === idx;
                  const isSavingThis = savingEdit?.type === 'todo' && savingEdit?.index === idx;
                  return (
                  <div key={idx} className={`profile-todo-item ${isEditingThis ? 'editing' : ''} ${isSavingThis ? 'saving' : ''}`}>
                    <div className="todo-checkbox" />
                    <div
                      className={`todo-content editable-content ${isEditingThis ? 'editing' : ''} ${isSavingThis ? 'saving' : ''}`}
                    >
                      <div
                        className="todo-text"
                        contentEditable={isEditingThis}
                        suppressContentEditableWarning={true}
                        ref={isEditingThis ? editInputRef : null}
                        onInput={(e) => isEditingThis && setEditingItem({ ...editingItem, value: e.currentTarget.innerText })}
                        onKeyDown={(e) => {
                          if (isEditingThis) {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveEdit();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              e.stopPropagation();
                              cancelEdit();
                            }
                          }
                        }}
                      >
                        {taskText}
                      </div>
                      {task?.due && <div className="todo-due">{formatRelativeDate(task.due)}</div>}
                      {!isEditingThis && !isSavingThis && (
                        <button
                          className="hover-edit-icon"
                          onClick={() => !editingItem && startEditing('todo', taskText, idx)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                      {isEditingThis && (
                        <button className="edit-confirm-btn" onClick={(e) => { e.stopPropagation(); saveEdit(); }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );})
              )}
            </div>
          )}

          {/* Documents Section */}
          <div className="profile-section documents-section">
            <div className="section-label">
              Documents
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleDocumentUpload}
                style={{ display: 'none' }}
              />
              <button
                className="document-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <span className="upload-spinner" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                )}
              </button>
            </div>

            {uploadProgress && (
              <div className={`upload-progress ${uploadProgress.includes('Error') ? 'error' : ''}`}>
                {uploadProgress}
              </div>
            )}

            {documents.length === 0 && !isUploading && (
              <div className="documents-empty">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span>No documents yet</span>
              </div>
            )}

            <div className="documents-list">
              {documents.map(doc => (
                <div key={doc.id} className="document-item">
                  <div className="document-icon">
                    {doc.file_type?.includes('pdf') ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <path d="M9 15h6M9 11h6" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <path d="M16 13H8M16 17H8M10 9H8" />
                      </svg>
                    )}
                  </div>
                  <div className="document-info">
                    <div className="document-name">{doc.file_name}</div>
                    <div className="document-meta">
                      {formatFileSize(doc.file_size)}
                      {doc.aiSummary?.summary && (
                        <span className="document-summary">{doc.aiSummary.summary}</span>
                      )}
                    </div>
                  </div>
                  <div className="document-actions">
                    <button
                      className="document-action-btn"
                      onClick={() => handleDocumentDownload(doc)}
                      title="Download"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                      </svg>
                    </button>
                    <button
                      className="document-action-btn delete"
                      onClick={() => handleDocumentDelete(doc.id)}
                      title="Delete"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Edit Modal */}
      {contactModalOpen && (
        <div className="contact-modal-backdrop" onClick={() => setContactModalOpen(false)}>
          <div className="contact-modal" onClick={e => e.stopPropagation()}>
            <div className="contact-modal-header">
              <div className="contact-modal-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="contact-modal-title">
                <h3>Edit Contact</h3>
              </div>
              <button className="contact-modal-close" onClick={() => setContactModalOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="contact-modal-body">
              <input
                type="text"
                className="contact-name-input"
                value={contactForm.name}
                onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                placeholder="Full name"
                autoFocus
              />
              <div className="contact-fields-grid">
                <div className="contact-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="contact-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={e => setContactForm({ ...contactForm, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div className="contact-field">
                  <label>Organization</label>
                  <input
                    type="text"
                    value={contactForm.organization}
                    onChange={e => setContactForm({ ...contactForm, organization: e.target.value })}
                    placeholder="Company or organization"
                  />
                </div>
                <div className="contact-field">
                  <label>Title</label>
                  <input
                    type="text"
                    value={contactForm.title}
                    onChange={e => setContactForm({ ...contactForm, title: e.target.value })}
                    placeholder="Job title or role"
                  />
                </div>
                <div className="contact-field full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    value={contactForm.address}
                    onChange={e => setContactForm({ ...contactForm, address: e.target.value })}
                    placeholder="Street, City, State ZIP"
                  />
                </div>
              </div>
            </div>
            <div className="contact-modal-footer">
              <div className="contact-modal-hint">
                <kbd>Esc</kbd> to close
              </div>
              <div className="contact-modal-actions">
                <button className="contact-btn-cancel" onClick={() => setContactModalOpen(false)}>
                  Cancel
                </button>
                <button className="contact-btn-save" onClick={saveContactInfo} disabled={savingContact}>
                  {savingContact ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chatbar now handled by persistent pagechat in DesktopApp */}
    </div>
  );
}

export default DesktopProfileView;
